import * as React from 'react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { heroContent } from '@/content/homepage';
import { usePrefersReducedMotion } from '@/hooks/use-prefers-reduced-motion';
import { assetUrl } from '@/lib/asset-url';
import { cn } from '@/lib/utils';

const SLIDE_INTERVAL_MS = 3000;

/**
 * ICONIQ Impact–style hero with a black slideshow frame.
 * The slideshow sits in row 1 alongside the heading and pitch,
 * so its bottom aligns with the top of the subhead paragraph below.
 */
export function HeroSection() {
  return (
    <section
      aria-labelledby="hero-heading"
      className="relative isolate overflow-hidden px-4 pt-14 pb-16 sm:px-6 sm:pt-20 sm:pb-24 lg:px-8 lg:pt-24 lg:pb-28"
    >
      <div className="mx-auto grid w-full max-w-7xl gap-10 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)] lg:items-stretch lg:gap-x-16 lg:gap-y-0">
        {/* Row 1, Col 1 — logo + brand + model + pitch */}
        <div className="flex flex-col gap-8">
          <div>
            <h1
              id="hero-heading"
              className="font-display text-foreground flex flex-col gap-0 font-bold tracking-[-0.035em] uppercase"
            >
              <span className="sr-only">{heroContent.brand}</span>
              {heroContent.brandLines.map((line, index) => (
                <span
                  key={line}
                  aria-hidden
                  className={
                    index === 0
                      ? "block text-[clamp(2.6rem,9.5vw,6.25rem)] normal-case leading-[0.9]"
                      : "block text-[clamp(1.3rem, 4.75vw, 3.125rem)] normal-case leading-[0.9]"
                  }
                >
                  {line}
                </span>
              ))}
            </h1>

            <p className="text-foreground mt-8 max-w-xl text-xl font-medium tracking-tight text-balance sm:text-2xl lg:text-[1.7rem] lg:leading-snug">
              {heroContent.modelTitle}
            </p>
            <p className="text-primary mt-4 max-w-xl text-base font-semibold tracking-tight sm:text-lg">
              {heroContent.pitch}
            </p>
          </div>
        </div>

        {/* Row 1, Col 2 — slideshow (stretches to match row-1 text height) */}
        <HeroSlideshow />

        {/* Row 2, Col 1 — subhead + CTAs (starts right where the slideshow ends) */}
        <div className="flex flex-col gap-8 lg:col-start-1 lg:pt-5">
          <p className="text-muted-foreground max-w-xl text-base leading-relaxed sm:text-lg">
            {heroContent.subhead}
          </p>

          <div className="flex flex-col items-start gap-4 sm:flex-row sm:flex-wrap sm:items-center">
            <Button asChild size="lg" variant="cta" className="min-w-44">
              <a href={heroContent.primaryCta.href}>{heroContent.primaryCta.label}</a>
            </Button>
            <Button asChild size="lg" variant="outline" className="min-w-44 bg-card">
              <a href={heroContent.secondaryCta.href}>{heroContent.secondaryCta.label}</a>
            </Button>
            <Button asChild variant="link" className="text-primary h-auto px-0 text-base">
              <Link to={heroContent.tertiaryCta.href}>
                {heroContent.tertiaryCta.label}
                <span aria-hidden className="ml-1">
                  →
                </span>
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}

function HeroSlideshow() {
  const slides = heroContent.heroSlides;
  const reducedMotion = usePrefersReducedMotion();
  const [index, setIndex] = React.useState(0);

  const goTo = React.useCallback(
    (next: number) => {
      const count = slides.length;
      setIndex(((next % count) + count) % count);
    },
    [slides.length],
  );

  React.useEffect(() => {
    if (reducedMotion || slides.length <= 1) return;
    const timer = window.setInterval(() => {
      setIndex((current) => (current + 1) % slides.length);
    }, SLIDE_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [reducedMotion, slides.length]);

  const active = slides[index] ?? slides[0];

  return (
    <div className="flex h-full flex-col justify-start">
      <figure
        className="relative aspect-[720/692] w-full overflow-hidden rounded-2xl bg-black"
        aria-label="Hero image slideshow"
        aria-roledescription="carousel"
      >
        {slides.map((slide, slideIndex) => {
          const visible = slideIndex === index;
          const fadeClass = cn(
            'absolute inset-0',
            reducedMotion ? 'transition-none' : 'transition-opacity duration-700 ease-out',
            visible ? 'opacity-100' : 'opacity-0',
          );

          return (
            <img
              key={slide.src}
              src={assetUrl(slide.src)}
              alt={visible ? slide.alt : ''}
              className={cn(fadeClass, 'h-full w-full object-contain object-center')}
              decoding={slideIndex === 0 ? 'sync' : 'async'}
              fetchPriority={slideIndex === 0 ? 'high' : 'low'}
              aria-hidden={!visible}
            />
          );
        })}
        <figcaption className="sr-only" aria-live="polite">
          {active ? `Slide ${index + 1} of ${slides.length}: ${active.alt}` : null}
        </figcaption>
      </figure>

      {slides.length > 1 ? (
        <div
          className="mt-3 flex items-center justify-center gap-1"
          role="group"
          aria-label="Choose slide"
        >
          {slides.map((slide, slideIndex) => {
            const selected = slideIndex === index;
            return (
              <button
                key={slide.src}
                type="button"
                aria-current={selected ? 'true' : undefined}
                aria-label={`Show slide ${slideIndex + 1}: ${slide.alt}`}
                className="flex h-8 w-8 items-center justify-center rounded-full focus-visible:ring-2 focus-visible:ring-[#FFD100] focus-visible:ring-offset-2 focus-visible:outline-none"
                onClick={() => goTo(slideIndex)}
              >
                <span
                  aria-hidden
                  className={cn(
                    'rounded-full transition-colors',
                    selected
                      ? 'h-2.5 w-2.5 bg-[#FFD100]'
                      : 'h-2 w-2 bg-[#d4d4d4] hover:bg-[#bdbdbd]',
                  )}
                />
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
